import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Modal from '@/components/_common/Modal/Modal';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { insertWordBreaks } from '@/_utils/utils';
import { AssetType, InventoryFileEntity } from '@/redux/slices/slice_types';
import VideoPlayerAdvanced from '@/components/_common/VideoPlayerAdvanced/VideoPlayerAdvanced';
import { Alert, Grid } from '@mui/material';
import useContentPlayback from '@/components/_common/VideoPlayerAdvanced/useContentPlayback';

type File = InventoryFileEntity & { section?: string };

type VideoPlayerModalProps = {
  files: File[] | null;
  open: boolean;
  onClose: () => void;
  rootTitleId: number;
};

const VideoPlayerModal = ({ open, onClose, files, rootTitleId }: VideoPlayerModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getPlaybackSourceUrls, dolbyAudioTracks, dolbyVideoUrl, sourceUrls } = useContentPlayback();

  const videoFile = useMemo(() => {
    return files?.find((file) => (file.file_category ?? file.section) === 'video');
  }, [files]);

  const otherFiles = useMemo(() => {
    return files
      ?.filter((file) => (file.file_category ?? file.section) !== 'video')
      ?.map((file) => ({
        ...file,
        asset_type: (file.file_category || file.section || 'other') as AssetType,
        proxy: {
          url: sourceUrls[file.id],
          filename: file.name
        }
      }));
  }, [files, sourceUrls]);

  useEffect(() => {
    const fetchSources = async () => {
      setError(null);
      setLoading(true);
      try {
        await getPlaybackSourceUrls(files || [], rootTitleId);
      } catch (error) {
        console.error('Error fetching playback source URLs:', error);
        setError('There was a problem retrieving one of the files.');
      }
      setLoading(false);
    };
    if (open && files?.length) {
      fetchSources();
    }
  }, [files, open, rootTitleId]);

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      loading={loading}
      icon={<PolarisIcon color="warning" icon="preview" />}
      title={
        <>
          <strong>Preview:</strong> {videoFile ? insertWordBreaks(videoFile.name) : ''}
        </>
      }
    >
      {error && (
        <Grid container justifyContent="center" sx={{ mt: 8 }}>
          <Alert severity="error" sx={{ display: 'inline-flex' }}>
            {error}
          </Alert>
        </Grid>
      )}
      <div>
        {videoFile && sourceUrls[videoFile.id] && (
          <VideoPlayerAdvanced
            disableInventoryModal
            id="video-player-modal"
            url={dolbyVideoUrl || sourceUrls[videoFile.id]}
            externalAssets={otherFiles}
            audioTracks={dolbyAudioTracks}
          />
        )}
      </div>
    </StyledModal>
  );
};

export default VideoPlayerModal;

const StyledModal = styled(Modal)`
  .modal-title-section {
    & > span:first-child {
      background: ${({ theme }) => theme.palette.error.main};
    }

    strong {
      color: ${({ theme }) => theme.palette.error.main};
    }

    svg {
      color: white;
    }
  }
`;
